import { useState } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import { AccountCircle } from "@mui/icons-material";
import { Avatar, Box, Button, Chip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useTranslation } from "react-i18next";

import MDBox from "@vendor/components/MDBox";
import MDTypography from "@vendor/components/MDTypography";
import backgroundImage from "../../../assets/images/profile.png";
import { useUserData } from "@shared/hooks/useUserData";
import { MainLayout } from "@shared/ui/MainLayout";
import { useUserProfileQuery } from "../hooks/useUserProfileQuery";
import ProfileDialog from "./ProfileDialog";
import { UpdateProfile } from "./UpdateProfile";

import type { ReactNode } from "react";
import type { Theme } from "@mui/material/styles";

export interface ProfileHeaderProps {
  children?: ReactNode;
}

export function ProfileHeader({ children }: ProfileHeaderProps) {
    const { t } = useTranslation();
    const { user } = useUserData();
    /**
     * OJO: esto se desestructuraba de useUserData(), que devuelve
     * { user, token, permissions, userSetting } y NADA mas. `rolCustom` siempre fue
     * undefined, asi que el rol debajo del nombre nunca se mostro. Se conserva el
     * comportamiento; de donde deberia salir el rol es una pregunta para el backend.
     */
    const rolCustom: string | undefined = undefined;
    const [openDialog, setOpenDialog] = useState(false);
    const [update, setUpdate] = useState(false);

    const { data: userData, isFetching } = useUserProfileQuery();

    const handleClose = () => setOpenDialog(false);
    const OnClickUpdate = () => setUpdate((prev) => !prev);

    return (
        <MainLayout loading={isFetching && !userData}>
            <MDBox position="relative" mb={5}>
                <MDBox
                    display="flex"
                    position="relative"
                    borderRadius="xl"
                    sx={{
                        backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }: Theme) =>
                            `${linearGradient(
                                rgba(gradients.info.main, 0.1),
                                rgba(gradients.info.state, 0.2)
                            )}, url(${backgroundImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        overflow: "hidden",
                        minHeight: "28rem",
                    }}
                />
                <Card
                    sx={{
                        position: "relative",
                        mt: -8,
                        mx: 3,
                        py: 2,
                        px: 2,
                    }}
                >
                    <Grid container spacing={3} sx={{ alignItems: "center" }}>
                        <Grid size={{ xs: 12, md: 6, lg: 8 }}>
                            <Box sx={{ display: "flex", flexDirection: "row", alignContent: "center", alignItems: "center" }}>
                                <Avatar sx={{ width: 56, height: 56, bgcolor: grey[900] }}>
                                    <AccountCircle sx={{ width: 36, height: 36 }} />
                                </Avatar>
                                <Box sx={{ ml: 1, height: "100%", lineHeight: 1 }}>
                                    <MDTypography variant="h5" fontWeight="medium">
                                        {userData !== undefined && userData?.firstName + " " + userData?.lastName}
                                    </MDTypography>
                                    <MDTypography variant="button" color="text" fontWeight="regular">
                                        {rolCustom}
                                    </MDTypography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid sx={{ ml: "auto" }} size={{ xs: 12, md: 6, lg: 4 }}>
                            <AppBar position="static">
                                <Grid container spacing={3} sx={{ alignItems: "center" }}>
                                    <Grid size={4}>
                                        <Button onClick={OnClickUpdate}>
                                            {!update ? t("updateData") : t("seeData")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </AppBar>
                        </Grid>
                        <Grid size={12}>
                            {!update && (
                                <Grid container spacing={3} sx={{ alignItems: "center" }}>
                                    <Grid size={12}>
                                        <Typography variant="h5">{t("personalInformation")}: </Typography>
                                    </Grid>
                                    <Grid size={12}>
                                        <Grid container>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("name")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.firstName}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("lastname")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.lastName}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("gender")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.gender}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("birthdate")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.dateOfBirth}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("areaCode")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.areaCode}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("phone")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.phoneNumber}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("passport")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.passportNumber}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("postalCode")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.postalCode}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("address")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.address}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("country")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.country}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("nationality")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.nationality}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("email")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.email}</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12}>
                                        <Typography variant="h5">{t("emergencyContact")}: </Typography>
                                    </Grid>
                                    <Grid size={12}>
                                        <Grid container>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("nameEmergencyContact")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.nameEmergencyContact}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("relationshipEmergencyContact")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.relationshipEmergencyContact}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("codePhoneEmergency")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.areaCodeEmergencyContact}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("phoneEmergencyContact")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.phoneNumberEmergencyContact}</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid size={12}>
                                        <Typography variant="h5">{t("professionalInformation")}: </Typography>
                                    </Grid>
                                    <Grid size={12}>
                                        <Grid container>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row" }}>
                                                    <Typography variant="h6">{t("corporateEmail")}: </Typography>
                                                    <Typography variant={"p" as "body1"} sx={{ fontSize: 15 }}>{userData?.companyEmail}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Box sx={{ display: "flex", flexDirection: "row", alignContent: "center", alignItems: "center" }}>
                                                    <Typography variant="h6">{t("professionalProfile")}: </Typography>
                                                    <Grid size={12}>
                                                        {userData?.professionalProfile.map((profile, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={profile}
                                                                style={{ backgroundColor: "black", color: "white", margin: 2 }}
                                                            />
                                                        ))}
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {update && userData && (
                                <UpdateProfile
                                    user={userData}
                                    onSuccess={() => setUpdate(false)}
                                />
                            )}
                        </Grid>
                    </Grid>
                    {children}
                </Card>
                {/* El dialog de cambio de contraseña siempre montado para no perder estado del form */}
                <ProfileDialog
                    handleClose={handleClose}
                    openDialog={openDialog}
                    userId={user?.id}
                />
            </MDBox>
        </MainLayout>
    );
}

